// External Intake & Collection v1.0 PR 4 §2.3 — party-side renderer for
// the full extended FormSchema (spec §4.1).
//
// Supports every field type the PR-2 template builder can author:
//   - data: text, textarea, number, currency, percentage, date, email,
//     phone, ssn, ein, dropdown, radio, checkbox, multi-select, yesno
//   - composite: address, fileattach, initials, datesigned
//   - signature (in-house): captures intent; the actual signing + PDF
//     locking is PR 8, so we render it as a placeholder field that the
//     recipient confirms with their typed name
//   - structural: sectionheading, instruction, pagebreak, repeatgroup
//
// Behavior: visibleWhen hides dependent fields, required-fields drive a
// blocking validation on submit, helpText renders beneath each input.
// Address + datesigned + initials are first-class types so the renderer
// can store them as structured objects / sensible defaults without the
// builder having to hand-roll composite groups.

import { useMemo } from "react";
import type { ReactElement } from "react";
import { Input, Textarea, FormField } from "@/design-system/components/ui";
import { isoDateInTz } from "@/design-system/lib/format";
import type { FormField as SchemaField, FormSchema, VisibleWhen } from "@/lib/formSchema";
import { emailFieldError } from "@/lib/emailValidator";
import { phoneFieldError } from "@/lib/phoneValidator";

export type PortalFormValue =
  | string
  | number
  | boolean
  | string[]
  | null
  | { [key: string]: PortalFormValue }
  | PortalFormValue[];

export type PortalFormData = { [key: string]: PortalFormValue };

interface RendererProps {
  schema: FormSchema;
  value: PortalFormData;
  onChange: (next: PortalFormData) => void;
  disabled?: boolean;
}

export function PortalFormRenderer({ schema, value, onChange, disabled }: RendererProps) {
  const pages = useMemo(() => splitPages(schema.fields), [schema]);

  return (
    <div className="flex flex-col gap-space-6">
      {pages.map((page, i) => (
        <div key={i} className="flex flex-col gap-space-4">
          {page.map((field) => (
            <FieldNode
              key={field.name}
              field={field}
              value={value}
              onChange={onChange}
              disabled={disabled}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

function splitPages(fields: SchemaField[]): SchemaField[][] {
  const pages: SchemaField[][] = [[]];
  for (const f of fields) {
    if (f.type === "pagebreak") {
      if (pages[pages.length - 1].length > 0) pages.push([]);
    } else {
      pages[pages.length - 1].push(f);
    }
  }
  return pages.filter((p) => p.length > 0);
}

interface FieldNodeProps {
  field: SchemaField;
  value: PortalFormData;
  onChange: (next: PortalFormData) => void;
  disabled?: boolean;
  /** When set, the field is rendered inside a repeat-group row; reads /
   *  writes go through the row instead of the top-level form data. */
  scope?: {
    row: PortalFormData;
    onRowChange: (next: PortalFormData) => void;
  };
}

function FieldNode({ field, value, onChange, disabled, scope }: FieldNodeProps) {
  if (!isVisible(field.visibleWhen, scope?.row ?? value)) return null;

  const get = (): PortalFormValue => {
    const source = scope?.row ?? value;
    return source[field.name];
  };

  const set = (next: PortalFormValue) => {
    if (scope) {
      scope.onRowChange({ ...scope.row, [field.name]: next });
    } else {
      onChange({ ...value, [field.name]: next });
    }
  };

  // Sender-Fields v1 — fields filled by our staff (or auto-resolved) are
  // shown to the recipient read-only; they review but never edit them.
  const effectiveDisabled = disabled || field.filledBy === "Sender";

  return renderField(field, get(), set, effectiveDisabled, () => (
    // recursive renderer for repeat-group nested fields
    <RepeatGroup
      field={field}
      rows={Array.isArray(get()) ? (get() as PortalFormData[]) : []}
      onRowsChange={(rows) => set(rows as unknown as PortalFormValue)}
      disabled={disabled}
    />
  ));
}

function renderField(
  field: SchemaField,
  fieldValue: PortalFormValue,
  set: (v: PortalFormValue) => void,
  disabled: boolean | undefined,
  renderRepeatGroup: () => ReactElement,
): ReactElement | null {
  switch (field.type) {
    case "sectionheading":
      return (
        <h3 key={field.name} className="text-h4 font-display text-onyx mt-space-2 mb-space-1 m-0">
          {field.text ?? field.label}
        </h3>
      );
    case "instruction":
      return (
        <p key={field.name} className="text-small text-gray-mid m-0">
          {field.text ?? field.label}
        </p>
      );
    case "pagebreak":
      return null;
    case "repeatgroup":
      return (
        <FormField
          key={field.name}
          label={field.label}
          required={field.required}
          helperText={field.helpText}
        >
          {renderRepeatGroup()}
        </FormField>
      );
    case "text":
    case "email":
    case "phone": {
      const raw = typeof fieldValue === "string" ? fieldValue : "";
      // Inline format validation mirrors the staff Lead/Broker forms
      // (PersonForm.tsx) — only flag once the user has typed something,
      // so empty fields are handled by the submit-time required check.
      const formatError =
        raw.trim().length === 0
          ? undefined
          : field.type === "email"
            ? emailFieldError(raw)
            : field.type === "phone"
              ? phoneFieldError(raw)
              : undefined;
      return (
        <FormField
          key={field.name}
          label={field.label}
          required={field.required}
          helperText={field.helpText}
          error={formatError}
        >
          <Input
            type={field.type === "email" ? "email" : field.type === "phone" ? "tel" : "text"}
            inputMode={field.type === "phone" ? "tel" : undefined}
            value={raw}
            disabled={disabled}
            invalid={!!formatError}
            onChange={(e) => set(e.target.value)}
          />
        </FormField>
      );
    }
    case "textarea":
      return (
        <FormField key={field.name} label={field.label} required={field.required} helperText={field.helpText}>
          <Textarea
            rows={4}
            value={typeof fieldValue === "string" ? fieldValue : ""}
            disabled={disabled}
            onChange={(e) => set(e.target.value)}
          />
        </FormField>
      );
    case "number":
    case "currency":
    case "percentage":
      return (
        <FormField key={field.name} label={field.label} required={field.required} helperText={field.helpText}>
          <Input
            type="number"
            inputMode="decimal"
            step={field.type === "number" ? "any" : "0.01"}
            value={typeof fieldValue === "number" ? String(fieldValue) : typeof fieldValue === "string" ? fieldValue : ""}
            disabled={disabled}
            onChange={(e) => set(e.target.value === "" ? null : Number(e.target.value))}
          />
        </FormField>
      );
    case "date":
      return (
        <FormField key={field.name} label={field.label} required={field.required} helperText={field.helpText}>
          <Input
            type="date"
            value={typeof fieldValue === "string" ? fieldValue : ""}
            disabled={disabled}
            onChange={(e) => set(e.target.value || null)}
          />
        </FormField>
      );
    case "ssn":
      return (
        <FormField key={field.name} label={field.label} required={field.required} helperText={field.helpText ?? "Format: 123-45-6789"}>
          <Input
            inputMode="numeric"
            placeholder="123-45-6789"
            value={typeof fieldValue === "string" ? fieldValue : ""}
            disabled={disabled}
            onChange={(e) => set(formatMaskedDigits(e.target.value, [3, 2, 4]))}
          />
        </FormField>
      );
    case "ein":
      return (
        <FormField key={field.name} label={field.label} required={field.required} helperText={field.helpText ?? "Format: 12-3456789"}>
          <Input
            inputMode="numeric"
            placeholder="12-3456789"
            value={typeof fieldValue === "string" ? fieldValue : ""}
            disabled={disabled}
            onChange={(e) => set(formatMaskedDigits(e.target.value, [2, 7]))}
          />
        </FormField>
      );
    case "dropdown":
      return (
        <FormField key={field.name} label={field.label} required={field.required} helperText={field.helpText}>
          <select
            className="w-full h-10 px-space-3 border border-border-light rounded-md bg-white-pure text-body sm:text-small text-onyx focus:outline-none focus:border-gold focus:shadow-goldRing disabled:opacity-60"
            value={typeof fieldValue === "string" ? fieldValue : ""}
            disabled={disabled}
            onChange={(e) => set(e.target.value || null)}
          >
            <option value="">Select…</option>
            {(field.options ?? []).map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </FormField>
      );
    case "radio":
      return (
        <FormField key={field.name} label={field.label} required={field.required} helperText={field.helpText}>
          <div className="flex flex-col gap-space-2">
            {(field.options ?? []).map((o) => (
              <label key={o.value} className="flex items-center gap-space-2 text-small text-onyx">
                <input
                  type="radio"
                  name={field.name}
                  value={o.value}
                  checked={fieldValue === o.value}
                  disabled={disabled}
                  onChange={(e) => set(e.target.value)}
                />
                {o.label}
              </label>
            ))}
          </div>
        </FormField>
      );
    case "yesno":
      return (
        <FormField key={field.name} label={field.label} required={field.required} helperText={field.helpText}>
          <div className="flex gap-space-2">
            {(["yes", "no"] as const).map((opt) => {
              const isOn = (typeof fieldValue === "string" && fieldValue === opt)
                || (typeof fieldValue === "boolean" && fieldValue === (opt === "yes"));
              return (
                <button
                  type="button"
                  key={opt}
                  disabled={disabled}
                  onClick={() => set(opt === "yes")}
                  className={`px-space-3 py-space-1 text-small rounded-md border ${
                    isOn ? "bg-gold/20 border-gold text-onyx" : "bg-white-pure border-border-light text-gray-mid"
                  }`}
                >
                  {opt === "yes" ? "Yes" : "No"}
                </button>
              );
            })}
          </div>
        </FormField>
      );
    case "checkbox":
      return (
        <label key={field.name} className="flex items-start gap-space-2 text-small text-onyx">
          <input
            type="checkbox"
            checked={!!fieldValue}
            disabled={disabled}
            onChange={(e) => set(e.target.checked)}
            className="mt-[2px]"
          />
          <span>
            <span className="block">
              {field.label}
              {field.mustBeChecked && (
                <span className="text-gold ml-1" aria-hidden data-testid={`must-be-checked-${field.name}`}>
                  *
                </span>
              )}
            </span>
            {field.helpText && <span className="block text-caption text-gray-mid mt-space-1">{field.helpText}</span>}
          </span>
        </label>
      );
    case "multi-select": {
      const selected = Array.isArray(fieldValue) ? (fieldValue as string[]) : [];
      return (
        <FormField key={field.name} label={field.label} required={field.required} helperText={field.helpText}>
          <div className="flex flex-wrap gap-space-2">
            {(field.options ?? []).map((o) => {
              const isOn = selected.includes(o.value);
              return (
                <button
                  key={o.value}
                  type="button"
                  disabled={disabled}
                  onClick={() => set(isOn ? selected.filter((s) => s !== o.value) : [...selected, o.value])}
                  className={`px-space-2 py-[3px] text-caption rounded-pill border ${
                    isOn ? "bg-gold/20 border-gold text-onyx" : "bg-white-pure border-border-light text-gray-mid"
                  }`}
                >
                  {o.label}
                </button>
              );
            })}
          </div>
        </FormField>
      );
    }
    case "address": {
      const addr: { [key: string]: PortalFormValue } =
        fieldValue && typeof fieldValue === "object" && !Array.isArray(fieldValue)
          ? (fieldValue as { [key: string]: PortalFormValue })
          : {};
      const partOf = (k: string): string => (typeof addr[k] === "string" ? (addr[k] as string) : "");
      const setPart = (key: string, v: string) => set({ ...addr, [key]: v });
      return (
        <FormField key={field.name} label={field.label} required={field.required} helperText={field.helpText}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-space-2">
            <Input placeholder="Street" value={partOf("street")} disabled={disabled} onChange={(e) => setPart("street", e.target.value)} />
            <Input placeholder="Unit / Apt" value={partOf("unit")} disabled={disabled} onChange={(e) => setPart("unit", e.target.value)} />
            <Input placeholder="City" value={partOf("city")} disabled={disabled} onChange={(e) => setPart("city", e.target.value)} />
            <Input placeholder="State" value={partOf("state")} disabled={disabled} onChange={(e) => setPart("state", e.target.value)} />
            <Input placeholder="ZIP" value={partOf("zip")} disabled={disabled} onChange={(e) => setPart("zip", e.target.value)} />
          </div>
        </FormField>
      );
    }
    case "fileattach":
      return (
        <FormField key={field.name} label={field.label} required={field.required} helperText={field.helpText ?? "Attach a file relevant to this answer."}>
          <input
            type="file"
            disabled={disabled}
            onChange={(e) => {
              const f = e.target.files?.[0];
              set(f ? f.name : null);
            }}
            className="text-small"
          />
          {typeof fieldValue === "string" && fieldValue && (
            <p className="text-caption text-gray-mid mt-space-1">Attached: {fieldValue}</p>
          )}
        </FormField>
      );
    case "signature":
      return (
        <FormField
          key={field.name}
          label={field.label}
          required={field.required}
          helperText={field.helpText ?? "Type your full name to sign. The signed document is generated when you submit."}
        >
          <Input
            placeholder="Type your full name"
            value={typeof fieldValue === "string" ? fieldValue : ""}
            disabled={disabled}
            onChange={(e) => set(e.target.value)}
          />
        </FormField>
      );
    case "initials":
      return (
        <FormField key={field.name} label={field.label} required={field.required} helperText={field.helpText ?? "Initial here."}>
          <Input
            placeholder="e.g. JD"
            maxLength={6}
            value={typeof fieldValue === "string" ? fieldValue : ""}
            disabled={disabled}
            onChange={(e) => set(e.target.value.toUpperCase())}
          />
        </FormField>
      );
    case "datesigned": {
      // Read-only date that auto-fills today. We display today for empty
      // values without writing back during render (React forbids it); the
      // recipient's local copy stays blank until they intentionally
      // interact elsewhere, and on submit we persist the displayed value.
      // The portal has no per-viewer timezone yet, so this resolves to the
      // app's zone — what every other date on this surface already shows. It
      // stops being the UTC day: the field is disabled and read-only, so a
      // recipient signing after 17:00 Phoenix signed TOMORROW with no control
      // to correct it.
      const today = isoDateInTz(new Date());
      const v = typeof fieldValue === "string" && fieldValue ? fieldValue : today;
      return (
        <FormField key={field.name} label={field.label} required={field.required} helperText={field.helpText}>
          <Input type="date" value={v} disabled readOnly />
        </FormField>
      );
    }
    default:
      return null;
  }
}

interface RepeatGroupProps {
  field: SchemaField;
  rows: PortalFormData[];
  onRowsChange: (rows: PortalFormData[]) => void;
  disabled?: boolean;
}

function RepeatGroup({ field, rows, onRowsChange, disabled }: RepeatGroupProps) {
  const children = field.fields ?? [];
  return (
    <div className="flex flex-col gap-space-3">
      {rows.map((row, idx) => (
        <div
          key={idx}
          className="border border-border-light rounded-md p-space-3 bg-white-warm/40"
          data-testid={`repeat-row-${field.name}-${idx}`}
        >
          <div className="flex items-center justify-between mb-space-2">
            <span className="text-caption text-gray-mid">#{idx + 1}</span>
            {!disabled && (
              <button
                type="button"
                onClick={() => onRowsChange(rows.filter((_, i) => i !== idx))}
                className="text-caption text-status-error underline"
              >
                Remove
              </button>
            )}
          </div>
          <div className="flex flex-col gap-space-3">
            {children.map((child) => (
              <FieldNode
                key={child.name}
                field={child}
                value={{}}
                onChange={() => {}}
                disabled={disabled}
                scope={{
                  row,
                  onRowChange: (next) =>
                    onRowsChange(rows.map((r, i) => (i === idx ? next : r))),
                }}
              />
            ))}
          </div>
        </div>
      ))}
      {!disabled && (
        <button
          type="button"
          onClick={() => onRowsChange([...rows, {}])}
          className="self-start text-small text-onyx underline"
        >
          + {field.addAnotherLabel ?? "Add another"}
        </button>
      )}
    </div>
  );
}

// SECOND IMPLEMENTATION: this rule is ported to C# in
// src/PhoenixCapital.Infrastructure/Services/Collection/CollectionFormResponseRenderer.cs
// (IsVisible), which decides whether the signed submitted-file document reports
// a question as answered/skipped or as "not shown". ADD A NEW OPERATOR IN BOTH
// PLACES. The C# port fails toward SHOWN on an operator it does not recognize,
// so forgetting it degrades a signed attestation rather than breaking it -- but
// the document then describes the form the recipient saw slightly wrongly.
function isVisible(rule: VisibleWhen | undefined, data: PortalFormData): boolean {
  if (!rule) return true;
  const v = data[rule.field];
  if ("equals" in rule && rule.equals !== undefined) {
    return v === rule.equals;
  }
  if (rule.in && rule.in.length > 0) {
    return rule.in.includes(v as string | number | boolean);
  }
  return true;
}

function formatMaskedDigits(input: string, groups: number[]): string {
  const digits = input.replace(/\D/g, "").slice(0, groups.reduce((a, b) => a + b, 0));
  const out: string[] = [];
  let pos = 0;
  for (const g of groups) {
    if (pos >= digits.length) break;
    out.push(digits.slice(pos, pos + g));
    pos += g;
  }
  return out.join("-");
}

// External-use helpers: lift schema validation to the submit boundary.

export function findFirstMissingRequired(
  schema: FormSchema,
  data: PortalFormData,
): SchemaField | null {
  for (const field of schema.fields) {
    // Owner Recapitalization Loan (ruling Q10) — a "mustBeChecked" checkbox is
    // missing until it is `true`. A plain `required` checkbox keeps today's
    // rule below (an unchecked box stores `false`, which passes). The server
    // applies the same rule (Core/Common/CollectionMustBeChecked).
    const mustBeChecked = field.type === "checkbox" && field.mustBeChecked === true;
    if (!field.required && !mustBeChecked) continue;
    // Sender-Fields v1 — sender-filled fields are never the recipient's to
    // complete, so a required sender field must not block their submit.
    if (field.filledBy === "Sender") continue;
    // datesigned is auto-populated with today's date on display; nothing
    // for the recipient to fill in, so it's never the "first missing".
    if (field.type === "datesigned") continue;
    if (!isVisible(field.visibleWhen, data)) continue;
    const v = data[field.name];
    if (mustBeChecked) {
      if (v !== true) return field;
      continue;
    }
    if (field.type === "repeatgroup") {
      if (!Array.isArray(v) || v.length === 0) return field;
      continue;
    }
    if (v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0)) {
      return field;
    }
  }
  return null;
}

export interface FormatError {
  field: SchemaField;
  message: string;
}

/** Submit-time guard for email / phone fields. Mirrors the format checks
 *  the backend applies on staff Lead/Broker create — same helpers used by
 *  PersonForm.tsx — so portal form responses don't slip through with
 *  malformed contact data. Recurses into repeat-group rows and respects
 *  visibleWhen so hidden fields don't block submit. */
export function findFirstFormatError(
  schema: FormSchema,
  data: PortalFormData,
): FormatError | null {
  return findFormatErrorInFields(schema.fields, data);
}

function findFormatErrorInFields(
  fields: SchemaField[],
  data: PortalFormData,
): FormatError | null {
  for (const field of fields) {
    if (!isVisible(field.visibleWhen, data)) continue;
    if (field.type === "repeatgroup") {
      const rows = Array.isArray(data[field.name])
        ? (data[field.name] as PortalFormData[])
        : [];
      for (const row of rows) {
        const nested = findFormatErrorInFields(field.fields ?? [], row);
        if (nested) return nested;
      }
      continue;
    }
    if (field.type !== "email" && field.type !== "phone") continue;
    const raw = typeof data[field.name] === "string" ? (data[field.name] as string) : "";
    if (raw.trim().length === 0) continue;
    const message =
      field.type === "email" ? emailFieldError(raw) : phoneFieldError(raw);
    if (message) return { field, message };
  }
  return null;
}
